/**
 * The login page's second-factor session: pick a method, run its challenge,
 * verify, and hand back the normal `/api/login` success body. The host keeps
 * the view state; this owns the token and the sequencing.
 */

import type { LoginMfaApi, LostDeviceResponse } from '@dealr/api-account';

/** The lost-device ID check's pseudo-method on `/login/mfa/verify`. */
export const IDENTITY_CHECK_METHOD_UUID = 'identity_check';
import type { LoginMfaMethod, LoginSuccessBody, StepUpProof } from '@dealr/core-account/domain';
import {
  SESSION_APPROVAL_METHOD_UUID,
  SESSION_APPROVAL_TIMEOUT_MS,
  approvalMatchNumber,
  defaultMethod,
  hasStrengthInfo,
  isBackupCodeMethod,
  isSessionApprovalMethod,
  loginVerifyPending,
  sessionApprovalPollDelay,
  sortMethodsByStrength,
  strongestMethod,
  type LoginMfaPending,
} from '@dealr/core-account';
import type { WebAuthnPort } from './ports';
import { SecondFactorError, codeProof, startSecondFactor, type SecondFactorStart } from './secondFactor';

/** The host's clock and timer, so the poll stays on its framework's scheduler (AngularJS `$timeout`). */
export interface SessionApprovalWait {
  delay(ms: number): PromiseLike<unknown>;
  now(): number;
  /** Checked before every poll; true stops the wait and resolves null. */
  cancelled(): boolean;
  /** The number to show while waiting ("Pick 47 on your other device"), once the prompt exists. */
  matchNumber?(matchNumber: number | null): void;
}

export interface LoginMfaSession {
  /** The methods the picker lists. With strength ratings, strongest first. Never a backup code. */
  readonly methods: LoginMfaMethod[];
  readonly initialMethod: LoginMfaMethod | null;
  /**
   * `account-security`: the strongest strong method, which the
   * picker selects up front. Null without strength ratings, so the picker
   * behaves as before.
   */
  readonly preferredMethod: LoginMfaMethod | null;
  /** `account-security`: the "Use a backup code" entry, when the server listed one. */
  readonly backupMethod: LoginMfaMethod | null;
  /** `account-security`: "Approve from another signed-in device", when the server listed it. */
  readonly sessionApprovalMethod: LoginMfaMethod | null;
  /**
   * Asks the user's other session to approve, then polls verify (202 while
   * waiting) with backoff. Reports the number to match through
   * `wait.matchNumber`. Resolves the login body, or null when cancelled;
   * rejects on denial (401 `approval_denied`), expiry, or after `SESSION_APPROVAL_TIMEOUT_MS`.
   */
  waitForSessionApproval(wait: SessionApprovalWait): Promise<LoginSuccessBody | null>;
  /** Runs the method's challenge; a security key verifies straight through and resolves `{done}`. */
  choose(method: LoginMfaMethod): Promise<{ step: SecondFactorStart; done?: LoginSuccessBody }>;
  submitCode(method: LoginMfaMethod, code: string): Promise<LoginSuccessBody>;
  /** Lost device: starts an ID check or a recovery request for this prompt. */
  recover(option: 'identity_check' | 'recovery_request'): Promise<LostDeviceResponse>;
  /**
   * After the lost-device ID check: `pending` (202) while it runs, the
   * one-time sign-in body when it passed (carries `mfa_enrollment_required`).
   * Rejects (401) when it failed.
   */
  identityCheckResult(): Promise<LoginSuccessBody | null>;
}

export function createLoginMfaSession(api: LoginMfaApi, pending: LoginMfaPending, webauthn: WebAuthnPort | null): LoginMfaSession {
  const token = pending.mfaToken;
  const verify = (proof: StepUpProof) =>
    api.verify({ mfa_token: token, ...proof } as Parameters<LoginMfaApi['verify']>[0]) as Promise<LoginSuccessBody>;
  const rated = hasStrengthInfo(pending.methodStrength);
  const listed = pending.methods.filter((method) => !isBackupCodeMethod(method) && !isSessionApprovalMethod(method));
  const methods = rated ? sortMethodsByStrength(listed, pending.methodStrength) : listed;
  return {
    methods,
    initialMethod: defaultMethod(methods),
    preferredMethod: rated ? strongestMethod(methods, pending.methodStrength) : null,
    backupMethod: pending.methods.find((method) => isBackupCodeMethod(method)) ?? null,
    sessionApprovalMethod: pending.methods.find((method) => isSessionApprovalMethod(method)) ?? null,
    async waitForSessionApproval(wait) {
      const started = wait.now();
      const reply = await api.challenge({ mfa_token: token, method_uuid: SESSION_APPROVAL_METHOD_UUID });
      wait.matchNumber?.(approvalMatchNumber(reply));
      for (let attempt = 0; ; attempt++) {
        if (wait.cancelled()) return null;
        if (wait.now() - started >= SESSION_APPROVAL_TIMEOUT_MS) {
          throw new SecondFactorError('No one approved this sign-in in time. Try again or choose another method.');
        }
        await wait.delay(sessionApprovalPollDelay(attempt));
        if (wait.cancelled()) return null;
        const result = await api.verify({ mfa_token: token, method_uuid: SESSION_APPROVAL_METHOD_UUID });
        if (!loginVerifyPending(result)) return result as LoginSuccessBody;
      }
    },
    async choose(method) {
      const step = await startSecondFactor(method, {
        challenge: (uuid) => api.challenge({ mfa_token: token, method_uuid: uuid }),
        webauthn,
      });
      if (step.needsCode) return { step };
      return { step, done: await verify(step.proof) };
    },
    submitCode(method, code) {
      return verify(codeProof(method, code));
    },
    recover(option) {
      if (!api.recovery) return Promise.reject(new SecondFactorError('This option isn’t available right now.'));
      return api.recovery({ mfa_token: token, option });
    },
    async identityCheckResult() {
      const result = await api.verify({ mfa_token: token, method_uuid: IDENTITY_CHECK_METHOD_UUID } as Parameters<LoginMfaApi['verify']>[0]);
      return loginVerifyPending(result) ? null : (result as LoginSuccessBody);
    },
  };
}
